import { Box, Button, Divider, Flex, Spinner, Text } from '@chakra-ui/react';
import { PermissionBadge } from 'modules/User';
import { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplyFont, ApplyFontsButtonByTypeProps, ApplyFontsButtonProps, FontTextProps } from './ApplyFonts.types';

const FontText = ({ children, fontSize, fontFamily, style, fontWeight }: FontTextProps) => (
  <Text
    lineHeight='150%'
    whiteSpace='nowrap'
    textOverflow='ellipsis'
    overflow='hidden'
    fontWeight={fontWeight || 'bold'}
    fontSize={fontSize}
    fontFamily={fontFamily}
    style={style}
  >
    {children}
  </Text>
);

const hoverStyle = { bg: 'hover.gray' };
export const ApplyFontsButton = memo(
  ({
    title,
    heading,
    paragraph,
    titleStyle,
    onClick,
    titleDisplayName,
    headingDisplayName,
    paragraphDisplayName,
    hasBrandKitPermission = true,
    maxW,
  }: ApplyFontsButtonProps) => {
    const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });
    const loaderRef = useRef<HTMLDivElement>(null);

    // manually show/hide loader instead of using state (since apply fonts can block the UI for a little bit)
    const showLoader = (isLoading: boolean) => {
      const loaderRefEl = loaderRef.current;
      if (loaderRefEl) loaderRefEl.style.setProperty('display', isLoading ? 'flex' : 'none', 'important');
    };

    const onClickFontBox = () => {
      const fontConfig = { title, heading, paragraph };
      onClick(fontConfig, showLoader);
    };

    return (
      <Box
        position='relative'
        border='1px solid'
        borderColor='gray.200'
        cursor='pointer'
        borderRadius='lg'
        p={3}
        onClick={onClickFontBox}
        as='button'
        textAlign='left'
        aria-label={t('applyFontButtonLabel')}
        role='group'
        maxW={maxW}
        _hover={hoverStyle}
      >
        {!hasBrandKitPermission && <PermissionBadge permission={'business'} position={'absolute'} top={-2} right={2} />}
        <Flex
          align='center'
          justify='center'
          bg='white'
          position='absolute'
          top='0'
          left='0'
          w='full'
          h='full'
          opacity='0.5'
          zIndex='1'
          display='none'
          ref={loaderRef}
        >
          <Spinner size='sm' />
        </Flex>
        <FontText fontSize='lg' fontFamily={title} style={titleStyle}>
          {titleDisplayName || title}
        </FontText>
        <FontText fontSize='sm' fontFamily={heading}>
          {headingDisplayName || heading}
        </FontText>
        <FontText fontSize='xs' fontWeight='normal' fontFamily={paragraph}>
          {paragraphDisplayName || paragraph}
        </FontText>
      </Box>
    );
  },
);
ApplyFontsButton.displayName = 'FontBox';

export const ApplyFontsButtonByType = memo(
  ({
    title,
    heading,
    paragraph,
    onClick,
    titleDisplayName,
    headingDisplayName,
    paragraphDisplayName,
    hasBrandKitPermission = true,
  }: ApplyFontsButtonByTypeProps) => {
    const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu' });
    const loaderRef = useRef<HTMLDivElement>(null);

    // manually show/hide loader instead of using state (since apply fonts can block the UI for a little bit)
    const showLoader = (isLoading: boolean) => {
      const loaderRefEl = loaderRef.current;
      if (loaderRefEl) loaderRefEl.style.setProperty('display', isLoading ? 'flex' : 'none', 'important');
    };

    const onClickFontBox = (fontConfig: Partial<ApplyFont>) => onClick(fontConfig, showLoader);

    return (
      <Flex position='relative' mt='1rem' border='1px solid' borderColor='gray.100' flexDir='column' borderRadius='4px'>
        {!hasBrandKitPermission && <PermissionBadge permission={'business'} position={'absolute'} top={-2} right={2} />}
        <Flex
          align='center'
          justify='center'
          bg='white'
          position='absolute'
          top='0'
          left='0'
          w='full'
          h='full'
          opacity='0.5'
          zIndex='1'
          display='none'
          ref={loaderRef}
          borderRadius='4px'
        >
          <Spinner size='sm' />
        </Flex>
        {Boolean(title) && (
          <>
            <Button
              variant='ghost'
              display='flex'
              justifyContent='space-between'
              p='1rem 12px'
              borderRadius='0'
              height='64px'
              aria-label={t('brandKit.applyFontButtonLabel')}
              onClick={() => onClickFontBox({ title })}
            >
              <Text as='h1' fontWeight='bold' fontSize='36px' fontFamily={title}>
                {t('textMenu.buttons.h1')}
              </Text>
              <Text as='span' fontWeight='medium' fontSize='xs' p='6px 8px' borderRadius='4px' bg='blue.50'>
                {titleDisplayName}
              </Text>
            </Button>
            <Box px='12px'>
              <Divider />
            </Box>
          </>
        )}
        {Boolean(heading) && (
          <>
            <Button
              variant='ghost'
              display='flex'
              justifyContent='space-between'
              p='1rem 12px'
              borderRadius='0'
              height='52px'
              aria-label={t('brandKit.applyFontButtonLabel')}
              onClick={() => onClickFontBox({ heading })}
            >
              <Text as='h2' fontWeight='bold' fontSize='24px' fontFamily={heading}>
                {t('textMenu.buttons.h2')}
              </Text>
              <Text as='span' fontWeight='medium' fontSize='xs' p='6px 8px' borderRadius='4px' bg='blue.50'>
                {headingDisplayName}
              </Text>
            </Button>
            <Box px='12px'>
              <Divider />
            </Box>
          </>
        )}
        {Boolean(paragraph) && (
          <Button
            variant='ghost'
            display='flex'
            justifyContent='space-between'
            p='1rem 12px'
            height='48px'
            borderRadius='0'
            aria-label={t('brandKit.applyFontButtonLabel')}
            onClick={() => onClickFontBox({ paragraph })}
          >
            <Text fontWeight='normal' fontSize='sm' fontFamily={paragraph}>
              {t('textMenu.buttons.paragraph')}
            </Text>
            <Text as='span' fontWeight='medium' fontSize='xs' p='6px 8px' borderRadius='4px' bg='blue.50'>
              {paragraphDisplayName}
            </Text>
          </Button>
        )}
      </Flex>
    );
  },
);

ApplyFontsButtonByType.displayName = 'FontBoxByType';
